import { ReactNode } from "react";

import { Divider } from "@mui/material";
import { ChartsTooltipContainer, useAxesTooltip } from "@mui/x-charts";

import { Column, OutlinedCard, Row } from "../containers";
import { Icon } from "../icons";
import { Text } from "../typography";
import { useAppPalette } from "../../theme";
import { ChartsLabel } from "./ChartsLabel";
import { useProjectionStartTime } from "./util";
import { TooltipContext, TooltipSeriesItem } from "./types";

export default function ChartsTooltip({
    enableProjection,
    isTimeAxis = true,
    dataset,
    renderTooltip
}: {
    enableProjection?: boolean;
    isTimeAxis?: boolean;
    dataset?: unknown[];
    renderTooltip?: (ctx: TooltipContext<unknown>) => ReactNode;
}) {
    const axesData = useAxesTooltip();
    const { paperBackground } = useAppPalette();
    const data = axesData?.[0];

    const { axisFormattedValue, axisValue, seriesItems, dataIndex } = data ?? {};

    const { now } = useProjectionStartTime();

    const isProjection = enableProjection && isTimeAxis && typeof axisValue === "number" && axisValue > now;

    const seriesWithMarkType =
        seriesItems?.map((s) => {
            const item: TooltipSeriesItem = {
                seriesId: s.seriesId === undefined ? undefined : String(s.seriesId),
                label: typeof s.formattedLabel === "string" ? s.formattedLabel : null,
                color: s.color,
                value: typeof s.value === "number" ? s.value : null,
                formattedValue: typeof s.formattedValue === "string" ? s.formattedValue : ""
            };
            return { ...item, markType: s.markType };
        }) ?? [];
    const series: TooltipSeriesItem[] = renderTooltip
        ? seriesWithMarkType.map(({ markType: _markType, ...rest }) => rest)
        : [];

    const dataPoint = typeof dataIndex === "number" ? dataset?.[dataIndex] : undefined;

    const ctx: TooltipContext<unknown> = {
        axisValue: typeof axisValue === "number" ? axisValue : undefined,
        axisFormattedValue: typeof axisFormattedValue === "string" ? axisFormattedValue : undefined,
        dataPoint,
        series
    };

    return (
        <ChartsTooltipContainer trigger="axis">
            <OutlinedCard padding={0} backgroundColor={paperBackground} sx={{ minWidth: 248 }}>
                {renderTooltip ? (
                    renderTooltip(ctx)
                ) : (
                    <Column py={1} spacing={1}>
                        <Row spaceBetween sx={{ px: 1 }}>
                            <Text variant="body2" sx={{ spacing: 0.5 }}>
                                {isTimeAxis && <Icon type="date" />}
                                {axisFormattedValue}
                            </Text>
                            {isProjection && (
                                <Text variant="body2" color="caption">
                                    {"Projected"}
                                </Text>
                            )}
                        </Row>
                        <Divider />
                        <Column px={1} spacing={0.5}>
                            {seriesWithMarkType.map(({ seriesId, value, formattedValue, label, color, markType }) => {
                                if (seriesId === undefined) return null;
                                // Skip series with no value at this point (e.g. unrealized PnL when
                                // there is no position) so the tooltip doesn't show an empty row.
                                if (value === null) return null;
                                return (
                                    <Row key={seriesId} spaceBetween spacing={2}>
                                        <ChartsLabel markType={markType} markColor={color} label={label} />
                                        <Text variant="body2">{formattedValue}</Text>
                                    </Row>
                                );
                            })}
                        </Column>
                    </Column>
                )}
            </OutlinedCard>
        </ChartsTooltipContainer>
    );
}
